import React from 'react';
import styled from 'styled-components';

import type { Text } from '@zillow/constellation';
import { FormField, FormHelp, Label, Paragraph, Textarea } from '@zillow/constellation';
import { StackedChildren } from '@zillow/zrs-comet';

import type { Section } from '../../../models/index';
import type { HTMLElementEvent } from '../../../models/Types';
import { PROPERTY_ADDRESS } from '../../../constants/index';
import { isMultiFamily } from './Utilities/MultiFamilyLandlordUtility';
import { FULLSTORY_EXCLUDE } from '../../../constants/analytics';

export const MultiFamilyTextarea = styled(Textarea)`
  &,
  &:focus {
    height: 7.125rem;
  }
`;

export const StyledTextarea = styled(Textarea)`
  &,
  &:focus {
    height: 5.625rem;
  }
`;

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function PropertyAddress_v1({ section, setSectionValues }: Props): JSX.Element {
  function onPropertyAddressChange(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.PROPERTY_ADDRESS]: value,
    });
  }

  function onDevelopmentAddressChange(event: HTMLElementEvent<typeof Text>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.DEVELOPMENT_ADDRESS]: value,
    });
  }

  const isMultiFamilyLandlord = isMultiFamily(section);
  const CustomTextarea = isMultiFamilyLandlord ? MultiFamilyTextarea : StyledTextarea;

  return (
    <StackedChildren size="xs">
      <Paragraph fontType="h6">What is the property’s address?</Paragraph>
      <FormField className={FULLSTORY_EXCLUDE}>
        <CustomTextarea
          // Do not fix spacing, necessary for it to render correctly.
          aria-label="property-address"
          placeholder={
            isMultiFamilyLandlord
              ? `Street / Unit Number,${' '}
City, State, ZIP`
              : `Street,${' '}
City, State, ZIP`
          }
          value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.PROPERTY_ADDRESS)}
          resize="vertical"
          error={section.hasFieldError(PROPERTY_ADDRESS.FIELD_NAME.PROPERTY_ADDRESS)}
          onChange={onPropertyAddressChange}
        />
        {section.hasFieldError(PROPERTY_ADDRESS.FIELD_NAME.PROPERTY_ADDRESS) && (
          <FormHelp error>{PROPERTY_ADDRESS.ERROR_MESSAGE.PROPERTY_ADDRESS}</FormHelp>
        )}
      </FormField>

      {isMultiFamilyLandlord && (
        <FormField className={FULLSTORY_EXCLUDE}>
          <Label id="development-address">
            Development address (if same as above, enter the property address without the Unit Number)
          </Label>
          <MultiFamilyTextarea
            // Do not fix spacing, necessary for it to render correctly.
            aria-labelledby="development-address"
            placeholder={`Development Name,${' '}
Street,${' '}
City, State ZIP`}
            resize="vertical"
            error={section.hasFieldError(PROPERTY_ADDRESS.FIELD_NAME.DEVELOPMENT_ADDRESS)}
            value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.DEVELOPMENT_ADDRESS)}
            onChange={onDevelopmentAddressChange}
          />
          {section.hasFieldError(PROPERTY_ADDRESS.FIELD_NAME.DEVELOPMENT_ADDRESS) && (
            <FormHelp error>{PROPERTY_ADDRESS.ERROR_MESSAGE.DEVELOPMENT_ADDRESS}</FormHelp>
          )}
        </FormField>
      )}
    </StackedChildren>
  );
}
